"use strict";

import { RequestUtil } from './common-util';

/** WebSecureInput client
 * This is an example of WebSecureInput integration.
 * This class initialize an iframe for WebSecureInput,
 * creates the MessageChannel between iframe and host page and manage communications between them.
 */
export class WebSecureInputClient {
  private apiUrl: string;
  private xIssuerId: string;
  private xUserId: string;
  private token: string;
  private cssUrl: string;
  private targetOrigin: string;
  private lifetime: number;
  private webSecureInputURL: string;
  private iframeURL: string;
  private iframe: any;
  private channel: any;
  public onInit: () => void;
  public onPinReady: () => void;
  public onIframeReady: () => void;
  public onPinNotReady: () => void;
  public onPinConfirmationError: () => void;
  public onSuccess: (body: any) => void;
  public onError: () => void;

  constructor(apiUrl: string, xIssuerId: string, xUserId: string, token: string, cssUrl: string, lifetime: number) {
    this.apiUrl = apiUrl;
    this.xIssuerId = xIssuerId;
    this.xUserId = xUserId;
    this.token = token;
    this.cssUrl = cssUrl;
    this.targetOrigin = apiUrl;
    this.lifetime = lifetime;
    this.webSecureInputURL = "/api/v1/dcs/cards/webSecureInputs";
    this.iframeURL = "/api/v1/sca/webSecureInputs/iframe";
    this.iframe = "";
    this.channel = "";
    //Bellow functions need to be overided by the host page that use this client.
    Object.defineProperty(this, "onInit", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.defineProperty(this, "onPinReady", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.defineProperty(this, "onIframeReady", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.defineProperty(this, "onPinNotReady", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.defineProperty(this, "onPinConfirmationError", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.defineProperty(this, "onSuccess", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.defineProperty(this, "onError", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.preventExtensions(this);
  }

  initializeIframe(
    containerId: string,
    type: string,
    issuerCardId: string,
    pinSize: number,
    confirmNewPin: boolean,
    keyboardType: string,
    encryptionType: string
  ): void {
    this._initializeIframe(
      containerId,
      type,
      issuerCardId,
      pinSize,
      confirmNewPin,
      keyboardType,
      encryptionType
    );
  }

  initializeIframeWithError(
    containerId: string,
    type: string,
    issuerCardId: string,
    pinSize: number,
    confirmNewPin: boolean,
    keyboardType: string,
    encryptionType: string
  ): void {
    //Make
    this.lifetime = 2;
    this._initializeIframe(
      containerId,
      type,
      issuerCardId,
      pinSize,
      confirmNewPin,
      keyboardType,
      encryptionType
    );
  }

  private _initializeIframe(
    containerId: string,
    type: string,
    issuerCardId: string,
    pinSize: number,
    confirmNewPin: boolean,
    keyboardType: string,
    encryptionType: string
  ): void {
    // Create iframe
    this.createIframe(containerId);
    //Initialize communication channel
    this.createChannel(
      type,
      issuerCardId,
      pinSize,
      confirmNewPin,
      keyboardType,
      encryptionType
    );
    // Manage event on iframe load
    this.iframe.addEventListener("load", () => this.onIframeLoad());
    // Manage the iframe destruction
    this.iframe.addEventListener("beforeunload", () => this.clearChannel());
    //Add iframe to dom
    this.addIframe(containerId);
  }

  // Send the initialization message to the iframe once it's loaded
  private onIframeLoad(): void {
    this.iframe.contentWindow.postMessage(
      "INIT",
      // Target only the iframe with the correct origin
      this.targetOrigin,
      // Transfer the 2nd port to the iframe so it can send back messages
      [this.channel.port2]
    );
  }

  //Add iframe to DOM
  private addIframe(containerId: string): void {
    //Attach ifrme to DOM
    const element = document.getElementById(containerId);
    const src = this.getIframePath();
    this.iframe.setAttribute("src", src);
    //Clear previous container content
    element.innerHTML = "";
    element.append(this.iframe);
  }

  // Function to close channel connection
  private clearChannel(): void {
    this.channel.port1.close.bind(this.channel.port1);
  }

  // Create iframe without add it to the dom.
  private createIframe(containerId: string): void {
    this.iframe = document.createElement("iframe");
    this.iframe.setAttribute("id", containerId + "-iframe");
    this.iframe.setAttribute("class", "secure-input-iframe");
  }

  private createChannel(type: string, issuerCardId: string, pinSize: number, confirmNewPin: boolean, keyboardType: string, encryptionType: string): void {
    this.channel = new MessageChannel();
    // We implement the onMessage function of MessageChannel to RECEIVE message FROM Iframe.
    this.channel.port1.onmessage = (event) => {
      const isWebInputDataInitialized =
        this.isWebInputInitializationData(event);
      this.onInit();
      if (!!isWebInputDataInitialized) {
        let webDisplayJson = {
          webInputInitializationData: event.data.webInputInitializationData,
          type,
          cssUrl: this.cssUrl,
          card: { issuerCardId },
          lifetime: this.lifetime,
          pinSize: pinSize,
          keyboardType: keyboardType,
          confirmNewPin,
          encryptionType
        };
        //Send the request to backend to create the webSecureInput
        this.postWebInputInitialization(webDisplayJson).then((data) => {
          if (data && data.webInputOpaqueData) {
            //Forward the webSecureInput response to the iframe
            this.sendWebSecureInputDataToIframe(data, pinSize);
          } else {
            this.onError();
          }
        });
      } else if (event.data.type === "INPUT_READY") {
        this.onPinReady();
      } else if (event.data.type === "IFRAME_READY") {
        this.onIframeReady();
      } else if (event.data.type === "INPUT_NOT_READY") {
        this.onPinNotReady();
      } else if (event.data.type === "INPUT_CONFIRMATION_ERROR") {
        this.onPinConfirmationError();
      } else if (event.data.type === "SUCCESS") {
        this.onSuccess(event.data);
      } else if (event.data.type === "ERROR") {
        this.onError();
      }
    };
  }

  //Generate the complete URL for Iframe
  private getIframePath(): string {
    const queryString = `X-Issuer-Id=${this.xIssuerId}&X-User-Id=${this.xUserId}`;
    return `${this.apiUrl}${this.iframeURL}?${encodeURI(queryString)}`;
  }

  //Verify the webdisplay initialization data type
  private isWebInputInitializationData(event: MessageEvent): boolean {
    return (
      event.data.type === "INIT_DATA" &&
      event.data.webInputInitializationData.startsWith("01.")
    );
  }

  //Send request to the backend in order to init web secure input
  private postWebInputInitialization(data: any): Promise<any> {
    return RequestUtil.postData(
      this.apiUrl + this.webSecureInputURL,
      data,
      this.xIssuerId,
      this.xUserId,
      this.token
    );
  }

  // Post SEND message to iframe to tell the iframe to send the repin request
  // It's here we really trigger the PIN CHANGE/SET
  sendSecureInputData(): void {
    this.iframe.contentWindow.postMessage(
      "SEND",
      // Target only the iframe with the correct origin
      this.targetOrigin
    );
  }

  private sendWebSecureInputDataToIframe(data: any, pinSize: number): void {
    //Send webDisplayOpaqueData to iframe
    const webInputOpaqueData = data.webInputOpaqueData;
    this.iframe.contentWindow.postMessage(
      { pinSize, webInputOpaqueData },
      this.targetOrigin
    );
  }
}
